import * as React from "react";

import { useFormContext } from "react-hook-form";
import { FormattedMessage } from "react-intl";

import { Button } from "@ps-design-system/components/Button";
import { CheckboxField } from "@ps-design-system/components/Checkbox";
import { Input } from "@ps-design-system/components/Input";
import { Label } from "@ps-design-system/components/Label";
import { Text } from "@ps-design-system/components/Text";

import { useIntl } from "react-intl-compat";
import type { AmenityOption } from "~/services/env.service";

import { ListingDetailsFormValues } from "../ListingDetailsForm.schema";

type AmenitiesFieldsProps = {
  amenityOptions: AmenityOption[];
};

const toTitleCase = (value: string) =>
  value.replace(
    /\w\S*/g,
    (word) => word.charAt(0).toUpperCase() + word.substring(1).toLowerCase(),
  );

const toSlug = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

/** The "Amenities" section: amenity checkboxes, and custom amenities. */
const AmenitiesFields = ({ amenityOptions }: AmenitiesFieldsProps) => {
  const form = useFormContext<ListingDetailsFormValues>();
  const intl = useIntl();

  const [draftAmenity, setDraftAmenity] = React.useState("");

  const customAmenityNames = Object.keys(form.watch("customAmenities") ?? {});

  const handleAddCustomAmenity = () => {
    form.setValue(`customAmenities.${toTitleCase(draftAmenity)}`, true, {
      shouldDirty: true,
    });
    setDraftAmenity("");
  };

  return (
    <div>
      <Text
        typeface="headline-2-m"
        element="p"
        className="tw-border-b tw-border-gray-300 tw-py-8 tw-mb-8"
        testId="listing-details-form-amenities-header"
      >
        <FormattedMessage
          id="hosting.listing_details.amenities_header"
          defaultMessage="Amenities"
        />
      </Text>

      {amenityOptions.map((amenity) => (
        <CheckboxField
          key={amenity.id}
          name={`amenities.${amenity.id}`}
          description={amenity.name}
          testId={`listing-details-form-amenity-${toSlug(amenity.id)}-checkbox`}
        />
      ))}
      {customAmenityNames.map((name) => (
        <CheckboxField
          key={name}
          name={`customAmenities.${name}`}
          description={name}
          testId={`listing-details-form-custom-amenity-${toSlug(name)}-checkbox`}
        />
      ))}

      <div className="tw-flex tw-flex-col tw-gap-8 tw-mt-16">
        <Label htmlFor="listing-details-custom-amenity">
          {intl.formatMessage({
            id: "hosting.listing_details.amenities_add_custom_label",
            defaultMessage: "Add your own amenities",
          })}
        </Label>
        <div className="tw-flex tw-gap-8 tw-max-w-[350px]">
          <Input
            fullWidth
            id="listing-details-custom-amenity"
            value={draftAmenity}
            placeholder={intl.formatMessage({
              id: "hosting.listing_details.amenities_placeholder",
              defaultMessage: "Amenity name",
            })}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDraftAmenity(e.target.value)
            }
            testId="listing-details-form-custom-amenity-input"
          />
          <Button
            onClick={handleAddCustomAmenity}
            disabled={!draftAmenity}
            testId="listing-details-form-custom-amenity-add-button"
          >
            <FormattedMessage
              id="hosting.listing_details.add_amenity"
              defaultMessage="Add"
            />
          </Button>
        </div>
      </div>
    </div>
  );
};

export { AmenitiesFields };
